// 구인 공고 등록/수정 페이지
import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Grid,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  Stack,
  Heading,
  useToast,
  Text,
  Tag,
  TagLabel,
  TagCloseButton,
  HStack,
  InputGroup,
  InputRightAddon,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import {
  getJobPosting,
  createJobPosting,
  updateJobPosting,
  getLocations
} from 'utils/firestoreUtils';
import { useAuth } from 'contexts/AuthContext';
import jobTypes from 'constants/jobTypes';

function JobPostingForm() {
  const { id } = useParams();
  const history = useHistory();
  const { currentUser, userProfile } = useAuth();
  const toast = useToast();

  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    jobType: '',
    location: '',
    workplaceId: '',
    periodStart: '',
    periodEnd: '',
    headcount: 1,
    wageType: '시간제',
    wageAmount: 0,
    requiredSkills: [],
    preferredSkills: [],
    status: 'open'
  });

  const [newRequiredSkill, setNewRequiredSkill] = useState('');
  const [newPreferredSkill, setNewPreferredSkill] = useState('');

  useEffect(() => {
    loadLocations();
    if (isEdit) {
      loadJobData();
    }
  }, [id]);

  async function loadLocations() {
    try {
      const locationList = await getLocations();
      setLocations(locationList);
    } catch (error) {
      console.error('위치 로딩 오류:', error);
    }
  }

  async function loadJobData() {
    try {
      const jobData = await getJobPosting(id);
      if (jobData) {
        setFormData({
          title: jobData.title || '',
          description: jobData.description || '',
          jobType: jobData.jobType || '',
          location: jobData.location || '',
          workplaceId: jobData.workplaceId || '',
          periodStart: jobData.periodStart || '',
          periodEnd: jobData.periodEnd || '',
          headcount: jobData.headcount || 1,
          wageType: jobData.wageType || '시간제',
          wageAmount: jobData.wageAmount || 0,
          requiredSkills: jobData.requiredSkills || [],
          preferredSkills: jobData.preferredSkills || [],
          status: jobData.status || 'draft'
        });
      }
    } catch (error) {
      console.error('공고 로딩 오류:', error);
      toast({
        title: '오류',
        description: '공고를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  function handleChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  function addRequiredSkill() {
    if (newRequiredSkill.trim()) {
      setFormData(prev => ({
        ...prev,
        requiredSkills: [...prev.requiredSkills, newRequiredSkill.trim()]
      }));
      setNewRequiredSkill('');
    }
  }

  function removeRequiredSkill(index) {
    setFormData(prev => ({
      ...prev,
      requiredSkills: prev.requiredSkills.filter((_, i) => i !== index)
    }));
  }

  function addPreferredSkill() {
    if (newPreferredSkill.trim()) {
      setFormData(prev => ({
        ...prev,
        preferredSkills: [...prev.preferredSkills, newPreferredSkill.trim()]
      }));
      setNewPreferredSkill('');
    }
  }

  function removePreferredSkill(index) {
    setFormData(prev => ({
      ...prev,
      preferredSkills: prev.preferredSkills.filter((_, i) => i !== index)
    }));
  }

  async function handleSubmit(e, isDraft = false) {
    e.preventDefault();

    // 유효성 검사
    if (!formData.title.trim()) {
      toast({
        title: '입력 오류',
        description: '공고 제목을 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.description.trim()) {
      toast({
        title: '입력 오류',
        description: '상세 설명을 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.location) {
      toast({
        title: '입력 오류',
        description: '근무 위치를 선택해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.jobType) {
      toast({
        title: '입력 오류',
        description: '업무 유형을 선택해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.periodStart || !formData.periodEnd) {
      toast({
        title: '입력 오류',
        description: '근무 기간을 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (formData.headcount < 1) {
      toast({
        title: '입력 오류',
        description: '모집 인원은 1명 이상이어야 합니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (formData.wageAmount <= 0) {
      toast({
        title: '입력 오류',
        description: '급여를 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);

      const jobData = {
        ...formData,
        // 수정 모드일 때는 formData의 status 사용, 아니면 draft/open 설정
        status: isEdit ? formData.status : (isDraft ? 'draft' : 'open')
      };

      if (isEdit) {
        await updateJobPosting(id, jobData);
        toast({
          title: '수정 완료',
          description: '공고가 성공적으로 수정되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        history.push(`/admin/job-postings/${id}`);
      } else {
        const jobId = await createJobPosting(jobData, currentUser.uid);
        toast({
          title: '등록 완료',
          description: '공고가 성공적으로 등록되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        history.push(`/admin/job-postings/${jobId}`);
      }
    } catch (error) {
      console.error('공고 저장 오류:', error);
      toast({
        title: '오류',
        description: '공고 저장에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }

  // 관리자가 아니면 접근 불가
  if (userProfile?.role !== 'admin' && userProfile?.role !== 'manager') {
    return (
      <Flex direction="column" pt={{ base: '120px', md: '75px' }} align="center">
        <Text>접근 권한이 없습니다.</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      {/* 헤더 영역 */}
      <Flex justify="space-between" align="center" mb={6}>
        <Button
          leftIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
          variant="ghost"
          size="sm"
        >
          목록으로
        </Button>
        <Heading size="lg">
          {isEdit ? '공고 수정' : '공고 등록'}
        </Heading>
      </Flex>

      {/* 폼 영역 */}
      <form onSubmit={(e) => handleSubmit(e, false)}>
        <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
          {/* 왼쪽 컬럼 - 기본 정보 */}
          <Card>
            <CardHeader p="6px 0px 22px 0px">
              <Heading size="md">기본 정보</Heading>
            </CardHeader>
            <CardBody>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">공고 제목</FormLabel>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="예: 카페 아르바이트 모집"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">상세 설명</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="업무 내용, 근무 조건 등을 상세히 입력해주세요"
                    minH="150px"
                  />
                </FormControl>

                  <FormControl isRequired>
                  <FormLabel fontSize="sm">근무 위치</FormLabel>
                  <Select
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="선택하세요"
                  >
                    {locations.map((location, index) => (
                      <option key={index} value={location}>{location}</option>
                    ))}
                  </Select>
                </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm">업무 유형</FormLabel>
                    <Select
                      value={formData.jobType}
                      onChange={(e) => handleChange('jobType', e.target.value)}
                      placeholder="선택하세요"
                    >
                      {jobTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Select>
                  </FormControl>
              </Stack>
            </CardBody>
          </Card>

          {/* 오른쪽 컬럼 - 근무 조건 */}
          <Card>
            <CardHeader p="6px 0px 22px 0px">
              <Heading size="md">근무 조건</Heading>
            </CardHeader>
            <CardBody>
              <Stack spacing={4}>
                <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">시작일</FormLabel>
                    <Input
                      type="date"
                      size="sm"
                      value={formData.periodStart}
                      onChange={(e) => handleChange('periodStart', e.target.value)}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm">종료일</FormLabel>
                    <Input
                      type="date"
                      size="sm"
                      value={formData.periodEnd}
                      onChange={(e) => handleChange('periodEnd', e.target.value)}
                    />
                  </FormControl>
                </Grid>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">모집 인원</FormLabel>
                  <NumberInput
                    value={formData.headcount}
                    onChange={(valueString) => handleChange('headcount', parseInt(valueString) || 1)}
                    min={1}
                    size="sm"
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>

                <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">급여 형태</FormLabel>
                    <Select
                      value={formData.wageType}
                      onChange={(e) => handleChange('wageType', e.target.value)}
                      size="sm"
                    >
                      <option value="시간제">시간제</option>
                      <option value="일급">일급</option>
                      <option value="주급">주급</option>
                      <option value="월급">월급</option>
                    </Select>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm">급여</FormLabel>
                    <InputGroup size="sm">
                      <NumberInput
                        value={formData.wageAmount}
                        onChange={(valueString) => handleChange('wageAmount', parseInt(valueString) || 0)}
                        min={0}
                        w="100%"
                      >
                        <NumberInputField />
                      </NumberInput>
                      <InputRightAddon>원</InputRightAddon>
                    </InputGroup>
                  </FormControl>
                </Grid>

                {isEdit && (
                  <FormControl>
                    <FormLabel fontSize="sm">공고 상태</FormLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => handleChange('status', e.target.value)}
                      size="sm"
                    >
                      <option value="draft">작성중</option>
                      <option value="open">모집중</option>
                      <option value="closed">마감</option>
                      <option value="selected">선정완료</option>
                      <option value="canceled">취소됨</option>
                    </Select>
                  </FormControl>
                )}
              </Stack>
            </CardBody>
          </Card>

          {/* 하단 전체 너비 - 자격 요건 */}
          <Card gridColumn={{ base: '1', lg: '1 / -1' }}>
            <CardHeader p="6px 0px 22px 0px">
              <Heading size="md">자격 요건 (선택)</Heading>
            </CardHeader>
            <CardBody>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                {/* 필수 조건 */}
                <FormControl>
                  <FormLabel fontSize="sm">필수 조건</FormLabel>
                  <Flex gap={2} mb={2}>
                    <Input
                      size="sm"
                      value={newRequiredSkill}
                      onChange={(e) => setNewRequiredSkill(e.target.value)}
                      placeholder="필수 조건 입력 후 추가"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addRequiredSkill();
                        }
                      }}
                    />
                    <Button size="sm" onClick={addRequiredSkill} colorScheme="blue">
                      추가
                    </Button>
                  </Flex>
                  <HStack spacing={2} flexWrap="wrap">
                    {formData.requiredSkills.map((skill, index) => (
                      <Tag key={index} size="sm" colorScheme="blue">
                        <TagLabel>{skill}</TagLabel>
                        <TagCloseButton onClick={() => removeRequiredSkill(index)} />
                      </Tag>
                    ))}
                  </HStack>
                </FormControl>

                {/* 우대 사항 */}
                <FormControl>
                  <FormLabel fontSize="sm">우대 사항</FormLabel>
                  <Flex gap={2} mb={2}>
                    <Input
                      size="sm"
                      value={newPreferredSkill}
                      onChange={(e) => setNewPreferredSkill(e.target.value)}
                      placeholder="우대 사항 입력 후 추가"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addPreferredSkill();
                        }
                      }}
                    />
                    <Button size="sm" onClick={addPreferredSkill} colorScheme="green">
                      추가
                    </Button>
                  </Flex>
                  <HStack spacing={2} flexWrap="wrap">
                    {formData.preferredSkills.map((skill, index) => (
                      <Tag key={index} size="sm" colorScheme="green">
                        <TagLabel>{skill}</TagLabel>
                        <TagCloseButton onClick={() => removePreferredSkill(index)} />
                      </Tag>
                    ))}
                  </HStack>
                </FormControl>
              </Grid>
            </CardBody>
          </Card>
        </Grid>

        {/* 하단 버튼 */}
        <Flex justify="flex-end" gap={3} mt={6}>
          <Button
            variant="ghost"
            onClick={() => history.goBack()}
          >
            취소
          </Button>
          <Button
            variant="outline"
            onClick={(e) => handleSubmit(e, true)}
            isLoading={loading}
          >
            임시저장
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={loading}
          >
            {isEdit ? '수정 완료' : '등록하기'}
          </Button>
        </Flex>
      </form>
    </Flex>
  );
}

export default JobPostingForm;

